import { useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { authApi } from "@/api/authApi";

interface LoginPageProps {
  onLoginSuccess: () => void;
}

export function LoginPage({ onLoginSuccess }: LoginPageProps) {
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<null | string>(null);
  const queryClient = useQueryClient();

  const loginMutation = useMutation({
    mutationFn: ({ password, username }: { password: string; username: string }) =>
      authApi.login(username, password),
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Неверные учётные данные";
      setError(message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth-status"] });
      queryClient.invalidateQueries({ queryKey: ["auth-me"] });
      onLoginSuccess();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    loginMutation.mutate({ password, username });
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-purple-50 via-white to-green-50">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 text-center">Вход в конфигуратор</h1>
          <p className="text-sm text-gray-600 text-center mt-2">Bot Orchestrator</p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
            <p className="text-sm text-red-800 font-semibold text-center">{error}</p>
          </div>
        )}

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2" htmlFor="username">
              Логин
            </label>
            <input
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              onChange={e => setUsername(e.target.value)}
              disabled={loginMutation.isPending}
              autoComplete="username"
              value={username}
              name="username"
              id="username"
              type="text"
              autoFocus
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2" htmlFor="password">
              Пароль
            </label>
            <input
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              onChange={e => setPassword(e.target.value)}
              disabled={loginMutation.isPending}
              autoComplete="current-password"
              value={password}
              name="password"
              type="password"
              id="password"
              required
            />
          </div>

          <button
            className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            disabled={loginMutation.isPending || !username || !password}
            type="submit"
          >
            {loginMutation.isPending ? "Вход..." : "Войти"}
          </button>
        </form>
      </div>
    </div>
  );
}
